import React, { useEffect, useRef, useState } from 'react'
import styled from "@emotion/styled";
import zglass from '../../../../assets/images/zglass.png';
import cloudbottom from '../../../../assets/images/cloudbottom.png';
import x from '../../../../assets/images/x.svg';
import tg from '../../../../assets/images/tg.svg';
import { tgLink, xLink } from '../../../../links';

const Wrapper = styled.section`
  width: 100%;
  padding: 78px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  @media (max-width: 720px) {
    min-height: auto;
    padding-top: 50px;
  }
`;

const Visual = styled.div`
  width: 100%;
  position: relative;
  min-height: 350px;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 720px) {
    min-height: 285px;
  }
`;

const GlassLogo = styled.img`
  width: 100%;
  max-width: 300px;
  height: auto;
  position: relative;
  z-index: 2;
  margin-top: 10px;
  will-change: transform, opacity;

  @media (max-width: 520px) {
    width: min(190px, 54vw);
  }
`;

const CloudImage = styled.img`
  width: 100%;
  min-width: 1180px;
  height: 430px;
  object-fit: cover;
  object-position: center top;
  position: absolute;
  left: 50%;
  bottom: -140px;
  z-index: 10;
  will-change: transform, opacity;

  @media (max-width: 720px) {
    min-width: 900px;
    bottom: 20px;
    height: 235px;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 160px auto 0;
  padding: 0 24px;
  text-align: center;
  will-change: transform, opacity;

  @media (max-width: 720px) {
    margin: 10px auto 0;
  }
`;

const Description = styled.p`
  color: #000;
  font-size: 18px;
  line-height: 1.22;
  margin: 0 auto 34px;

  @media (max-width: 520px) {
    font-size: 14px;
  }
`;

const Socials = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 82px;
  margin-bottom: 80px;

  @media (max-width: 520px) {
    gap: 48px;
  }
`;

const SocialLink = styled.a`
  color: #000;
  text-decoration: none;
  display: grid;
  justify-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 1;
`;

const SocialIcon = styled.img`
  width: 65px;
  height: 65px;
  object-fit: contain;

  @media (max-width: 520px) {
    width: 52px;
    height: 52px;
  }
`;

const Copyright = styled.div`
  width: 100%;
  background: #f0f0f0;
  color: #000;
  font-size: 14px;
  line-height: 1;
  text-align: center;
  padding: 18px 16px;
  will-change: transform, opacity;

  @media (max-width: 520px) {
    font-size: 12px;
  }
`;

const Footer = () => {
  const wrapperRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setScrollProgress(1);
      return undefined;
    }

    const handleScroll = () => {
      const rect = wrapper.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const animationStart = rect.height * 0.18;
      const animationDistance = rect.height * 0.82;
      const scrolledIntoFooterArt = windowHeight - rect.top - animationStart;
      const progress = Math.min(1, Math.max(0, scrolledIntoFooterArt / animationDistance));

      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const stage = (start, end) => {
    if (scrollProgress <= start) return 0;
    if (scrollProgress >= end) return 1;
    return (scrollProgress - start) / (end - start);
  };

  // Cloud comes first — early stage, rises from far below
  const cloudProgress = stage(0, 0.35);
  // Glass comes after cloud is settled — rises from below too
  const glassProgress = stage(0.35, 0.65);
  const contentProgress = stage(0.48, 0.82);
  const copyrightProgress = stage(0.72, 0.96);
const isMobile = window.innerWidth <= 720;
const glassOffset = isMobile ? 45 : 200;
  return (
    <Wrapper ref={wrapperRef}>
      <Visual>
        {/* Glass rises UP from bottom — starts after cloud */}
        <GlassLogo
          src={zglass}
          alt="Zephyr"
          style={{
            opacity: glassProgress,
               transform: `translateY(${glassOffset - glassProgress * glassOffset}px)`,
            transition: 'transform 0.1s ease-out, opacity 0.12s ease-out',
          }}
        />
        {/* Cloud rises UP from far bottom — comes first */}
        <CloudImage
          src={cloudbottom}
          alt=""
          aria-hidden="true"
          style={{
            opacity: cloudProgress,
            transform: `translateX(-50%) translateY(${300 - cloudProgress * 300}px)`,
            transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
          }}
        />
      </Visual>
      <Content
        style={{
          opacity: contentProgress,
          transform: `translateY(${44 - contentProgress * 44}px)`,
          transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
        }}
      >
        <Description>
          Zephyr removes identity leakage from Web3 interactions through disposable session wallets
          and autonomous browsing. Each wallet is created for a single interaction and destroyed
          afterward, preventing address tracking, behavior linking, and persistent on-chain identity
          exposure while enabling private, automated dApp interactions.
        </Description>
        <Socials>
          <SocialLink href={tgLink} target="_blank" rel="noreferrer">
            <SocialIcon src={tg} alt="Telegram" />
          </SocialLink>
          <SocialLink href={xLink} target="_blank" rel="noreferrer">
            <SocialIcon src={x} alt="X.com" />
          </SocialLink>
        </Socials>
      </Content>
      <Copyright
        style={{
          opacity: copyrightProgress,
          transform: `translateY(${26 - copyrightProgress * 26}px)`,
          transition: 'transform 0.12s ease-out, opacity 0.12s ease-out',
        }}
      >
        &copy; 2026 ZephyrBrowser. All rights reserved.
      </Copyright>
    </Wrapper>
  )
}

export default Footer